import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  FileText, 
  ClipboardCheck, 
  Shield, 
  Users, 
  Presentation,
  CreditCard,
  Sparkles,
  Edit,
  Trash2,
  Save,
  Download,
  History,
  Send,
  Loader2,
  Search,
  Filter,
  Eye,
  Clock,
  Upload,
  Paperclip,
  X
} from "lucide-react";
import axios from "axios";
import { formatAIResponse } from "@/utils/formatAIContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_ICONS = {
  competency_checklist: ClipboardCheck,
  educational_handout: FileText,
  policy_summary: Shield,
  orientation_guide: Users,
  inservice_outline: Presentation,
  quick_reference: CreditCard
};

const TEMPLATE_COLORS = {
  competency_checklist: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  educational_handout: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  policy_summary: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  orientation_guide: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  inservice_outline: "bg-pink-500/20 text-pink-400 border-pink-500/30",
  quick_reference: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
};

export default function TrainingCenter() {
  const [templates, setTemplates] = useState({});
  const [materials, setMaterials] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    topic: "",
    target_audience: "All Clinical Staff",
    department: "",
    additional_context: "",
    include_assessment: false
  });
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editContent, setEditContent] = useState("");
  
  // File attachment state
  const [attachedFile, setAttachedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, materialsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/training/templates`, { withCredentials: true }),
        axios.get(`${API}/training/materials`, { withCredentials: true }),
        axios.get(`${API}/training/assignments`, { withCredentials: true })
      ]);
      setTemplates(templatesRes.data.templates || {});
      setMaterials(materialsRes.data.materials || []);
      setAssignments(assignmentsRes.data.assignments || []);
    } catch (error) {
      console.error("Error fetching training data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file attachment for AI context
  const handleFileAttach = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      alert("Please attach a PDF, Word, or text file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be under 5MB");
      return;
    }
    
    setUploadingFile(true);
    setAttachedFile(file);
    
    try {
      if (fileExtension === '.txt' || fileExtension === '.md') {
        const text = await file.text();
        setFileContent(text.substring(0, 10000));
      } else {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/files/extract-text`, formDataUpload, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.text) {
          setFileContent(response.data.text.substring(0, 10000));
        } else {
          alert("Could not extract text from file");
          setAttachedFile(null);
        }
      }
    } catch (error) {
      console.error("Error processing file:", error);
      alert("Failed to process file. Try a text file instead.");
      setAttachedFile(null);
    } finally {
      setUploadingFile(false);
    }
  };
  
  const removeAttachedFile = () => {
    setAttachedFile(null);
    setFileContent("");
  };

  const handleGenerateContent = async () => {
    if (!selectedTemplate || !formData.topic) return;
    
    setGenerating(true);
    try {
      // Build context with file content if attached
      let enhancedContext = formData.additional_context || "";
      if (fileContent) {
        enhancedContext = `${enhancedContext}\n\n--- ATTACHED DOCUMENT CONTENT ---\n${fileContent}`;
      }
      
      const response = await axios.post(`${API}/training/generate`, {
        template_type: selectedTemplate,
        topic: formData.topic,
        target_audience: formData.target_audience,
        department: formData.department,
        additional_context: enhancedContext,
        include_assessment: formData.include_assessment
      }, { withCredentials: true });
      
      // Create the material with generated content
      const createResponse = await axios.post(`${API}/training/materials`, {
        title: formData.title || formData.topic,
        template_type: selectedTemplate,
        content: response.data.generated_content,
        target_audience: formData.target_audience,
        department: formData.department,
        tags: [],
        is_draft: true
      }, { withCredentials: true });
      
      setMaterials([createResponse.data, ...materials]);
      setShowCreateDialog(false);
      resetForm();
      
      // Open editor for the new material
      setEditingMaterial(createResponse.data);
      setEditContent(formatAIResponse(createResponse.data.content?.raw_content || ""));
      setShowEditDialog(true);
      
    } catch (error) {
      console.error("Error generating content:", error);
      alert("Failed to generate content. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateBlank = async () => {
    if (!selectedTemplate || !formData.title) return;
    
    setSaving(true);
    try {
      const template = templates[selectedTemplate];
      const blankContent = {
        raw_content: template.sections.map(s => `## ${s}\n\n[Add content here]\n`).join("\n"),
        sections: template.sections.reduce((acc, s) => ({ ...acc, [s]: "" }), {}),
        metadata: {
          template_type: selectedTemplate,
          target_audience: formData.target_audience
        }
      };
      
      const response = await axios.post(`${API}/training/materials`, {
        title: formData.title,
        template_type: selectedTemplate,
        content: blankContent,
        target_audience: formData.target_audience,
        department: formData.department,
        tags: [],
        is_draft: true
      }, { withCredentials: true });
      
      setMaterials([response.data, ...materials]);
      setShowCreateDialog(false);
      resetForm();
      
      // Open editor
      setEditingMaterial(response.data);
      setEditContent(blankContent.raw_content);
      setShowEditDialog(true);
      
    } catch (error) {
      console.error("Error creating material:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMaterial = async () => {
    if (!editingMaterial) return;
    
    setSaving(true);
    try {
      const updatedContent = {
        ...editingMaterial.content,
        raw_content: editContent,
        last_edited: new Date().toISOString()
      };
      
      const response = await axios.put(`${API}/training/materials/${editingMaterial.material_id}`, {
        content: updatedContent
      }, { withCredentials: true });
      
      setMaterials(materials.map(m => 
        m.material_id === editingMaterial.material_id ? response.data : m
      ));
      setEditingMaterial(response.data);
      
    } catch (error) {
      console.error("Error saving material:", error);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!editingMaterial) return;
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/training/materials/${editingMaterial.material_id}`, {
        is_draft: false
      }, { withCredentials: true });
      
      setMaterials(materials.map(m => 
        m.material_id === editingMaterial.material_id ? response.data : m
      ));
      setEditingMaterial(response.data);
      setShowEditDialog(false);
      
    } catch (error) {
      console.error("Error publishing material:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("Are you sure you want to delete this training material?")) return;
    
    try {
      await axios.delete(`${API}/training/materials/${materialId}`, { withCredentials: true });
      setMaterials(materials.filter(m => m.material_id !== materialId));
    } catch (error) {
      console.error("Error deleting material:", error);
    }
  };

  const handleRestoreVersion = async (version) => {
    if (!editingMaterial) return;
    
    try {
      const response = await axios.post(
        `${API}/training/materials/${editingMaterial.material_id}/restore/${version}`,
        {},
        { withCredentials: true }
      );
      setEditingMaterial(response.data);
      setEditContent(response.data.content?.raw_content || "");
      setShowHistoryDialog(false);
    } catch (error) {
      console.error("Error restoring version:", error);
    }
  };

  const resetForm = () => {
    setSelectedTemplate("");
    setFormData({
      title: "",
      topic: "",
      target_audience: "All Clinical Staff",
      department: "",
      additional_context: "",
      include_assessment: false
    });
    setAttachedFile(null);
    setFileContent("");
  };

  const filteredMaterials = materials.filter(m => {
    const matchesSearch = m.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === "all" || m.template_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const exportToPrint = () => {
    if (!editingMaterial) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Popup blocked. Please allow popups for this site to print.");
      return;
    }
    
    // Sanitize content by escaping HTML entities
    const sanitizeHtml = (str) => {
      if (!str) return '';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };
    
    const sanitizedTitle = sanitizeHtml(editingMaterial.title);
    const sanitizedType = sanitizeHtml(templates[editingMaterial.template_type]?.name || editingMaterial.template_type);
    const sanitizedAudience = sanitizeHtml(editingMaterial.target_audience || 'All Staff');
    const sanitizedContent = editContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>')
      .replace(/## /g, '</p><h2>')
      .replace(/<br\/><br\/>/g, '</h2><p>');
    
    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
<title>${sanitizedTitle}</title>
<style>
body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
h1 { color: #333; border-bottom: 2px solid #666; padding-bottom: 10px; }
h2 { color: #555; margin-top: 24px; }
p { line-height: 1.6; }
.meta { color: #888; font-size: 12px; margin-bottom: 20px; }
</style>
</head>
<body>
<h1>${sanitizedTitle}</h1>
<div class="meta">
Type: ${sanitizedType}<br/>
Audience: ${sanitizedAudience}<br/>
Version: ${editingMaterial.version || 1}
</div>
<div><p>${sanitizedContent}</p></div>
</body>
</html>`);
    doc.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            Training Center
          </h1>
          <p className="text-muted-foreground mt-1">Create, manage, and assign training materials</p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Material
        </Button>
      </div>

      {/* Template Quick Access */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(templates).map(([key, template]) => {
          const Icon = TEMPLATE_ICONS[key] || FileText;
          return (
            <button
              key={key}
              onClick={() => {
                setSelectedTemplate(key);
                setShowCreateDialog(true);
              }}
              className={`p-4 rounded-xl border ${TEMPLATE_COLORS[key]} hover:scale-[1.02] transition-all text-left`}
            >
              <Icon className="w-6 h-6 mb-2" />
              <p className="font-medium text-sm">{template.name}</p>
              <p className="text-xs opacity-70 mt-1 line-clamp-2">{template.description}</p>
            </button>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search materials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-700"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-700">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(templates).map(([key, template]) => (
              <SelectItem key={key} value={key}>{template.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Materials Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="all">All Materials ({materials.length})</TabsTrigger>
          <TabsTrigger value="drafts">Drafts ({materials.filter(m => m.is_draft).length})</TabsTrigger>
          <TabsTrigger value="published">Published ({materials.filter(m => !m.is_draft).length})</TabsTrigger>
          <TabsTrigger value="assignments">Assignments ({assignments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <MaterialsGrid 
            materials={filteredMaterials}
            templates={templates}
            onEdit={(m) => {
              setEditingMaterial(m);
              setEditContent(m.content?.raw_content || "");
              setShowEditDialog(true);
            }}
            onDelete={handleDeleteMaterial}
          />
        </TabsContent>

        <TabsContent value="drafts" className="mt-4">
          <MaterialsGrid 
            materials={filteredMaterials.filter(m => m.is_draft)}
            templates={templates}
            onEdit={(m) => {
              setEditingMaterial(m);
              setEditContent(m.content?.raw_content || "");
              setShowEditDialog(true);
            }}
            onDelete={handleDeleteMaterial}
          />
        </TabsContent>

        <TabsContent value="published" className="mt-4">
          <MaterialsGrid 
            materials={filteredMaterials.filter(m => !m.is_draft)}
            templates={templates}
            onEdit={(m) => {
              setEditingMaterial(m);
              setEditContent(m.content?.raw_content || "");
              setShowEditDialog(true);
            }}
            onDelete={handleDeleteMaterial}
          />
        </TabsContent>

        <TabsContent value="assignments" className="mt-4">
          <AssignmentsGrid assignments={assignments} />
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              Create Training Material
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Template Selection */}
            <div>
              <label className="text-sm font-medium text-zinc-300 mb-2 block">Template Type</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(templates).map(([key, template]) => {
                  const Icon = TEMPLATE_ICONS[key] || FileText;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedTemplate(key)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedTemplate === key 
                          ? 'border-violet-500 bg-violet-500/20' 
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-1 text-violet-400" />
                      <p className="text-sm font-medium text-white">{template.name}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedTemplate && (
              <>
                <Input
                  placeholder="Title (e.g., 'Hand Hygiene Competency Checklist')"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
                
                <Input
                  placeholder="Topic for AI generation (e.g., 'Restraint application for acute psychiatric patients')"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <Select 
                    value={formData.target_audience} 
                    onValueChange={(v) => setFormData({ ...formData, target_audience: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Target Audience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All Clinical Staff">All Clinical Staff</SelectItem>
                      <SelectItem value="RNs Only">RNs Only</SelectItem>
                      <SelectItem value="New Hires">New Hires</SelectItem>
                      <SelectItem value="Charge Nurses">Charge Nurses</SelectItem>
                      <SelectItem value="Mental Health Techs">Mental Health Techs</SelectItem>
                      <SelectItem value="All Staff">All Staff</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Input
                    placeholder="Department (optional)"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <Textarea
                  placeholder="Additional context or specific requirements..."
                  value={formData.additional_context}
                  onChange={(e) => setFormData({ ...formData, additional_context: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 h-24"
                />
                
                {/* File Attachment */}
                <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-zinc-300">
                    <Paperclip className="w-4 h-4 text-cyan-400" />
                    Attach Document for AI Context (Optional)
                  </div>
                  
                  {attachedFile ? (
                    <div className="flex items-center justify-between p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm text-cyan-300">{attachedFile.name}</span>
                      </div>
                      <button
                        onClick={removeAttachedFile}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-zinc-600 rounded-lg cursor-pointer hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-colors">
                      <input
                        type="file"
                        onChange={handleFileAttach}
                        accept=".pdf,.doc,.docx,.txt,.md"
                        className="hidden"
                      />
                      {uploadingFile ? (
                        <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                      ) : (
                        <Upload className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {uploadingFile ? "Processing..." : "Click to attach PDF, Word, or text file"}
                      </span>
                    </label>
                  )}
                </div>
                
                <label className="flex items-center gap-2 text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    checked={formData.include_assessment}
                    onChange={(e) => setFormData({ ...formData, include_assessment: e.target.checked })}
                    className="rounded border-zinc-600"
                  />
                  Include assessment/quiz questions
                </label>
              </>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCreateBlank}
              disabled={!selectedTemplate || !formData.title || saving}
              className="border-zinc-700"
            >
              <FileText className="w-4 h-4 mr-2" />
              Create Blank
            </Button>
            <Button
              onClick={handleGenerateContent}
              disabled={!selectedTemplate || !formData.topic || generating}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate with AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl h-[80vh] bg-zinc-900 border-zinc-700 flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-violet-400" />
                {editingMaterial?.title}
                {editingMaterial?.is_draft && (
                  <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-400">Draft</Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Version {editingMaterial?.version || 1}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowHistoryDialog(true)}
                  className="text-muted-foreground hover:text-white"
                >
                  <History className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden py-4">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full h-full bg-zinc-800 border-zinc-700 font-mono text-sm resize-none"
              placeholder="Enter your training material content..."
            />
          </div>
          
          <DialogFooter className="flex-shrink-0 gap-2">
            <Button variant="ghost" onClick={exportToPrint}>
              <Download className="w-4 h-4 mr-2" />
              Print / Export
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveMaterial}
              disabled={saving}
              className="border-zinc-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
            <Button
              onClick={handlePublish}
              disabled={saving}
              className="bg-gradient-to-r from-emerald-600 to-teal-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-violet-400" />
              Version History
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 py-4 max-h-96 overflow-y-auto">
            {editingMaterial?.version_history?.length > 0 ? (
              editingMaterial.version_history.slice().reverse().map((v, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700"
                >
                  <div>
                    <p className="font-medium text-white">Version {v.version}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(v.saved_at).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestoreVersion(v.version)}
                    className="border-zinc-600"
                  >
                    Restore
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No previous versions available
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Materials Grid Component
function MaterialsGrid({ materials, templates, onEdit, onDelete }) {
  if (materials.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No training materials yet</p>
        <p className="text-sm mt-1">Create your first material to get started</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {materials.map((material) => {
        const Icon = TEMPLATE_ICONS[material.template_type] || FileText;
        const template = templates[material.template_type];
        
        return (
          <Card 
            key={material.material_id}
            className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TEMPLATE_COLORS[material.template_type]?.split(' ')[0] || 'bg-zinc-700'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1">
                  {material.is_draft ? (
                    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">Draft</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-400">Published</Badge>
                  )}
                </div>
              </div>
              <CardTitle className="text-base mt-2">{material.title}</CardTitle>
              <CardDescription className="text-xs">
                {template?.name || material.template_type}
                {material.target_audience && ` • ${material.target_audience}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(material.updated_at).toLocaleDateString()}
                </span>
                <span>v{material.version || 1}</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 border-zinc-700"
                  onClick={() => onEdit(material)}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={() => onDelete(material.material_id)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Assignments Grid Component
function AssignmentsGrid({ assignments }) {
  if (assignments.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No training assignments yet</p>
        <p className="text-sm mt-1">Assign materials to staff to track completion</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {assignments.map((assignment) => {
        const completed = Object.values(assignment.completion_status || {}).filter(Boolean).length;
        const total = Object.keys(assignment.completion_status || {}).length;
        const progress = total > 0 ? (completed / total) * 100 : 0;
        
        return (
          <Card 
            key={assignment.assignment_id}
            className="bg-zinc-900/50 border-zinc-800"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{assignment.material_title}</CardTitle>
              <CardDescription className="text-xs">
                Assigned to {total} staff member{total !== 1 ? 's' : ''}
                {assignment.due_date && ` • Due: ${new Date(assignment.due_date).toLocaleDateString()}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Completion</span>
                  <span className="text-emerald-400">{completed}/{total}</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(assignment.completion_status || {}).map(([name, done]) => (
                  <Badge 
                    key={name}
                    variant="outline"
                    className={`text-xs ${done ? 'border-emerald-500/50 text-emerald-400' : 'border-zinc-600 text-zinc-400'}`}
                  >
                    {name} {done && '✓'}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
